/**
 * Book → Companion & Wellness → Group outing (Eli, 17 Oct 2026 — claude/spec "Volunteer Booking Redesign").
 * One question per screen: how many tenants → who they are → date and time → where (from 135 West 23rd Street to a
 * place on the list or a new one, which joins the list; a wellness walk or a community outing) → how they get there →
 * the volunteers. The app suggests one volunteer for every two tenants; the office may ask for more or fewer (a
 * board member or staff may come along). "Email all Companion & Wellness volunteers — N spots open" sends each Ready
 * volunteer their own link; "I accept — I will be there" fills the spots in the order they press. Volunteers never
 * get a tenant list. The tenants chosen are on the outing (the event) from the start; more are added or taken off on
 * the event's page, and VISIONS follows each tenant's own answers.
 */
import { useMemo, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { START_ADDRESS, minutesLabel, type OutingInput, type TransportKind } from '@shared/volunteers';
import type { BookProgram } from '@shared/book-programs';
import type { OutingView, SendResult } from '@shared/volunteers-api';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { TenantSearch } from '@/components/TenantSearch';
import { TimeSelect } from '@/components/TimeSelect';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { StepHead, SummaryCard } from '@/components/Steps';
import { useToast } from '@/components/ui/use-toast';
import { formatDayMedium } from '@/lib/dates';
import { errorText } from '@/features/people/api';
import { TextField } from '@/features/people/fields';
import { ProgramPicker } from '@/features/booking/ProgramPicker';
import { postJson, usePicker, useVolunteerRefresh } from './api';
import { DestinationPicker, TransportPicker } from './RequestSheet';
import { VolunteerChooser, savePlace } from './VolunteerChooser';

type Step = 'count' | 'who' | 'when' | 'where' | 'how' | 'volunteers';
const STEPS: Step[] = ['count', 'who', 'when', 'where', 'how', 'volunteers'];
type Person = { tenantId: string; unit: string; name: string };

/** One volunteer for every two tenants (1.4.0 §2), at least one. */
export const suggestedVolunteers = (tenants: number) => Math.max(1, Math.ceil(tenants / 2));

export function GroupOutingSheet({
  onClose,
  returnFocusTo,
  todayKey,
  first = null,
  onBack,
  onProgram,
}: {
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
  todayKey: string;
  /** A tenant already chosen on Book is the first one on the outing. */
  first?: Person | null;
  /** Back from the first question: to One-to-one / Group outing. */
  onBack?: () => void;
  onProgram?: (program: BookProgram) => void;
}) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const [step, setStep] = useState<Step>('count');
  const [count, setCount] = useState('4');
  const [people, setPeople] = useState<Person[]>(first ? [first] : []);
  const [day, setDay] = useState(`${todayKey.slice(0, 4)}-${todayKey.slice(4, 6)}-${todayKey.slice(6, 8)}`);
  const [minutes, setMinutes] = useState(10 * 60);
  const [place, setPlace] = useState<{ name: string; address: string; approved: boolean } | null>(null);
  const [kind, setKind] = useState<'CE' | 'GWW'>('CE');
  const [transport, setTransport] = useState<TransportKind | null>(null);
  const [spots, setSpots] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const howMany = Math.max(1, Math.min(60, Number(count) || 0));
  const dayKey = day.replace(/-/g, '');
  const tenants = Math.max(people.length, howMany);
  const suggested = suggestedVolunteers(tenants);
  const volunteers = spots === null ? suggested : Math.max(0, Math.min(200, Number(spots) || 0));
  const pick = usePicker(/^\d{8}$/.test(dayKey) ? { category: kind === 'GWW' ? 'walk' : 'trip', dayKey, minutes } : null);
  const reach = useMemo(() => pick.data?.all.length ?? 0, [pick.data]);
  const program = t('vol.program.companion');
  const n = STEPS.indexOf(step) + 1;

  const go = (s: Step) => {
    setProblem(null);
    setStep(s);
  };
  const refuse = (m: string) => {
    setProblem(m);
    announce(m, 'assertive');
  };
  const missing = (s: Step): string | null => {
    if (s === 'count') return Number(count) >= 1 ? null : t('vb.g.needCount');
    if (s === 'who') return people.length ? null : t('vb.g.needWho');
    if (s === 'when') return /^\d{8}$/.test(dayKey) ? null : t('activities.needDate');
    if (s === 'where') return place?.name ? null : t('vol.req.needDest');
    if (s === 'how') return transport ? null : t('vb.needTransport');
    return null;
  };
  const next = () => {
    const m = missing(step);
    if (m) return refuse(m);
    go(STEPS[n] ?? 'volunteers');
  };
  const back = () => (n === 1 ? onBack?.() : go(STEPS[n - 2]!));

  const create = async (send: boolean) => {
    for (const s of STEPS) {
      const m = missing(s);
      if (m) {
        go(s);
        return refuse(m);
      }
    }
    const body: OutingInput = {
      name: t(kind === 'GWW' ? 'vb.g.nameWalk' : 'vb.g.nameOuting', { place: place!.name }),
      dayKey,
      minutes,
      place: place!,
      description: '',
      volunteersNeeded: volunteers > 0 ? volunteers : null,
      tenantCapacity: tenants,
      kind,
      audience: 'companion',
      tenants: people.map((p) => p.tenantId),
      transport,
      send,
    };
    setBusy(true);
    try {
      await savePlace(postJson, place, t('vol.req.addedCategory'));
      const r = await postJson<{ outing: OutingView; send: SendResult }>('/api/volunteers/outings', body);
      toast(r.send.message);
      announce(r.send.message);
      refresh();
      onClose();
    } catch (e) {
      refuse(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode = null;
  if (step === 'count') {
    body = <TextField id="go-count" label={t('vb.g.countLabel')} value={count} onChange={(v) => setCount(v.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" required help={t('vb.g.countHelp')} />;
  } else if (step === 'who') {
    body = (
      <>
        <p className="help" aria-live="polite" data-testid="go-who-count">
          {t('vb.g.chosen', { n: people.length, of: howMany })}
        </p>
        {people.length ? (
          <ul className="chips dense" data-testid="go-people">
            {people.map((p) => (
              <li key={p.tenantId} className="chip">
                {p.unit} · {p.name}{' '}
                <button type="button" className="btn ghost sm" onClick={() => setPeople(people.filter((x) => x.tenantId !== p.tenantId))} aria-label={t('vb.g.remove', { name: p.name })} data-testid={`go-remove-${p.tenantId}`}>
                  <Icon name="x" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <TenantSearch
          onOpen={(x) => {
            if (people.some((p) => p.tenantId === x.tenantId)) return announce(t('vb.g.already', { name: x.name }));
            setPeople([...people, { tenantId: x.tenantId, unit: x.unit, name: x.name }]);
            announce(t('vb.g.added', { name: x.name }));
          }}
          label={t('vb.g.add')}
          placeholder={t('vol.req.tenantPh')}
          id="go-tenant-search"
          testId="go-tenant-search"
        />
        <p className="help">{t('vb.g.whoHelp')}</p>
      </>
    );
  } else if (step === 'when') {
    body = (
      <>
        <TextField id="go-date" label={t('vol.outing.date')} type="date" required value={day} onChange={setDay} />
        <TimeSelect id="go-time" label={t('vb.g.meetTime')} value={minutes} onChange={setMinutes} help={t('vb.g.meetHelp')} />
      </>
    );
  } else if (step === 'where') {
    body = (
      <>
        <p className="help" data-testid="go-start">
          {t('vb.startFrom', { address: START_ADDRESS })}
        </p>
        <DestinationPicker value={place} onChange={setPlace} idPrefix="go" required />
        <fieldset className="field">
          <legend className="eyebrow">{t('vol.outing.kind')}</legend>
          <div className="seg" role="group" aria-label={t('vol.outing.kind')}>
            <button type="button" aria-pressed={kind === 'GWW'} onClick={() => setKind('GWW')} data-testid="go-kind-walk">
              {t('vol.outing.kindGWW')}
            </button>
            <button type="button" aria-pressed={kind === 'CE'} onClick={() => setKind('CE')} data-testid="go-kind-outing">
              {t('vol.outing.kindCE')}
            </button>
          </div>
        </fieldset>
      </>
    );
  } else if (step === 'how') {
    body = (
      <div data-testid="go-how">
        <TransportPicker
          value={transport}
          onChange={(v) => {
            setTransport(v);
            go('volunteers');
          }}
          idPrefix="go"
        />
      </div>
    );
  } else {
    body = (
      <>
        <SummaryCard
          testId="go-summary"
          rows={[
            { label: t('vb.g.tenants'), value: `${people.length ? people.map((p) => p.name).join(', ') : '—'}${tenants > people.length ? ` · ${t('vb.g.places', { n: tenants })}` : ''}` },
            { label: t('vol.req.c.when'), value: `${/^\d{8}$/.test(dayKey) ? formatDayMedium(dayKey, lang) : '—'} · ${t('vb.g.meetAt', { time: minutesLabel(minutes) })}` },
            { label: t('vol.req.c.where'), value: `${t('vb.fromTo', { from: START_ADDRESS, to: place ? `${place.name}${place.address ? `, ${place.address}` : ''}` : '—' })}${transport ? ` · ${t(`vb.tr.${transport}`)}` : ''}` },
          ]}
        />
        <TextField
          id="go-spots"
          label={t('vb.g.spots')}
          value={spots ?? String(suggested)}
          onChange={(v) => setSpots(v.replace(/\D/g, '').slice(0, 3))}
          inputMode="numeric"
          help={t('vb.g.spotsHelp', { n: suggested, tenants })}
        />
        <VolunteerChooser pick={pick.data} loading={pick.isLoading} selected={null} onSelect={() => undefined} program={program} service="companion" allowPick={false} onClose={onClose} almostTestId="go-almost" />
      </>
    );
  }

  return (
    <SheetContent
      title={t('vb.g.title')}
      wide
      returnFocusTo={returnFocusTo}
      data-testid="group-outing-sheet"
      footer={
        <>
          <Button variant="secondary" onClick={back} data-testid="go-back">
            {t('activities.back')}
          </Button>
          {step === 'volunteers' ? (
            <>
              <Button variant="ghost" disabled={busy} onClick={() => create(false)} data-testid="go-save">
                {t('vb.saveOnly')}
              </Button>
              <Button disabled={busy || !reach} onClick={() => create(true)} data-testid="go-send">
                {t('vb.g.emailAll', { count: volunteers })}
              </Button>
            </>
          ) : step === 'how' && !transport ? null : (
            <Button onClick={next} data-testid="go-next">
              {t('activities.next')}
            </Button>
          )}
        </>
      }
    >
      {onProgram ? <ProgramPicker value="companion" onChange={onProgram} /> : null}
      <StepHead n={n} of={STEPS.length} question={t(`vb.g.q.${step}`)} id="go-q" testId="go-question" />
      {body}
      {problem ? (
        <p className="field-err" role="alert" data-testid="go-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
